import React from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Grid,
    Card,
    CardContent,
    Divider,
    Stack,
    Button
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import BalanceIcon from '@mui/icons-material/Balance';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import ModeNightIcon from '@mui/icons-material/ModeNight';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

interface ScheduleGridItem {
    id: string;
    name: string;
    area: string;
    position: string;
    limit_hours: number;
    total_hours: number;
    days: Record<string, {
        type: 'SHIFT' | 'ABSENCE' | 'REST';
        label: string;
        hours: number;
        start_time?: string;
        end_time?: string;
    }>;
}

interface Props {
    scheduleData: ScheduleGridItem[];
}

export const BorradorTurnosTable: React.FC<Props> = ({ scheduleData }) => {
    if (!scheduleData || scheduleData.length === 0) {
        return <Typography color="text.secondary">No hay datos para mostrar.</Typography>;
    }

    const firstItem = scheduleData[0];
    const dates = Object.keys(firstItem.days).sort();
    
    // Group by Area
    const groupedByArea = scheduleData.reduce((acc, curr) => {
        if (!acc[curr.area]) acc[curr.area] = [];
        acc[curr.area].push(curr);
        return acc;
    }, {} as Record<string, ScheduleGridItem[]>);

    const getDayName = (dateStr: string) => {
        return dayjs(dateStr).locale('es').format('dddd').toUpperCase();
    };

    const getWeekKey = (dateStr: string) => {
        const d = dayjs(dateStr);
        const day = d.day(); 
        const diffToMonday = day === 0 ? 6 : day - 1;
        return d.subtract(diffToMonday, 'day').format('YYYY-MM-DD');
    };

    const weeksMap = new Map<string, string[]>();
    dates.forEach(date => {
        const wk = getWeekKey(date);
        if (!weeksMap.has(wk)) weeksMap.set(wk, []);
        weeksMap.get(wk)!.push(date);
    });
    const weeks = Array.from(weeksMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    const getEmpWeeklyTotals = (emp: ScheduleGridItem) => {
        return weeks.map(([wk, wkDates]) => {
            return wkDates.reduce((sum, d) => sum + (emp.days[d]?.hours || 0), 0);
        });
    };

    const isExceeded = (emp: ScheduleGridItem) => {
        const totals = getEmpWeeklyTotals(emp);
        return totals.some(t => t > emp.limit_hours);
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Borrador Turnos');

        // Prepare Header to know column count
        const headerRow: any[] = ['ÁREA', 'NOMBRE'];
        weeks.forEach(([wk, wkDates], wkIdx) => {
            wkDates.forEach(date => {
                headerRow.push(`${getDayName(date)}\n${dayjs(date).format('DD/MM')}`);
            });
            headerRow.push(`TOTAL\nS${wkIdx + 1}`);
        });
        const totalColumns = headerRow.length;

        // Row 1: Cargo
        const cargoName = firstItem.position.toUpperCase();
        const cargoRow = sheet.addRow([`Cargo: ${cargoName}`]);
        cargoRow.font = { bold: true, size: 12, color: { argb: 'FF555555' } };
        sheet.mergeCells(1, 1, 1, totalColumns);

        // Row 2: Space
        sheet.addRow([]);

        // Row 3: HORARIO SEMANAL – 44 HORAS EFECTIVAS
        const bannerRow = sheet.addRow(['HORARIO SEMANAL – 44 HORAS EFECTIVAS']);
        bannerRow.height = 30;
        sheet.mergeCells(3, 1, 3, totalColumns);
        const bannerCell = bannerRow.getCell(1);
        bannerCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F3C7A' } };
        bannerCell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 14 };
        bannerCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Row 4: Table Header
        const header = sheet.addRow(headerRow);
        header.height = 30;

        // Style header
        header.eachCell((cell, colNumber) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: colNumber <= 2 ? 'FF0F3C7A' : (headerRow[colNumber-1].includes('TOTAL') ? 'FFE65100' : 'FF0F3C7A') }
            };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // Add Data
        const areaColors = ['FFF4A261', 'FF85C179', 'FF5EA8CD', 'FFE76F51', 'FFE9C46A'];
        let startRow = 5;

        Object.entries(groupedByArea).forEach(([area, employees], areaIndex) => {
            const areaBg = areaColors[areaIndex % areaColors.length];
            
            employees.forEach((emp, empIndex) => {
                const rowData: any[] = [area.toUpperCase(), emp.name.toUpperCase()];
                
                weeks.forEach(([wk, wkDates], wkIdx) => {
                    const weekTotal = wkDates.reduce((sum, d) => sum + (emp.days[d]?.hours || 0), 0);
                    const isExceededWeek = weekTotal > emp.limit_hours;
                    
                    wkDates.forEach((date) => {
                        const dayData = emp.days[date];
                        if (dayData) {
                           rowData.push(`${dayData.label}\n${dayData.hours > 0 ? dayData.hours + ' h' : '0 h'}`);
                        } else {
                           rowData.push(`-\n0 h`);
                        }
                    });
                    
                    rowData.push(`${weekTotal}h ${isExceededWeek ? '*' : ''}`);
                });
                
                const row = sheet.addRow(rowData);
                row.height = 40;
                
                // Style row
                row.eachCell((cell, colNumber) => {
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    
                    if (colNumber === 1) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: areaBg } };
                        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    } else if (colNumber === 2) {
                        cell.font = { bold: true };
                        const isEven = empIndex % 2 === 0;
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEven ? 'FFFDFDFD' : 'FFF5F5F5' } };
                    } else {
                        const isTotalCol = headerRow[colNumber - 1].includes('TOTAL');
                        if (isTotalCol) {
                            const weekIdxStr = headerRow[colNumber - 1].replace('TOTAL\nS', '');
                            const weekIdx = parseInt(weekIdxStr) - 1;
                            const [wk, wkDates] = weeks[weekIdx];
                            const weekTotal = wkDates.reduce((sum, d) => sum + (emp.days[d]?.hours || 0), 0);
                            const isExceededWeek = weekTotal > emp.limit_hours;

                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isExceededWeek ? 'FFFFEBEE' : 'FFFFF3E0' } };
                            cell.font = { bold: true, color: { argb: isExceededWeek ? 'FFC62828' : 'FFE65100' } };
                        } else {
                            let dateFound = '';
                            let colCounter = 3;
                            for (let w = 0; w < weeks.length; w++) {
                                const wDates = weeks[w][1];
                                for (let d = 0; d < wDates.length; d++) {
                                    if (colCounter === colNumber) {
                                        dateFound = wDates[d];
                                        break;
                                    }
                                    colCounter++;
                                }
                                colCounter++; 
                                if (dateFound) break;
                            }
                            
                            if (dateFound && emp.days[dateFound]) {
                                const dayData = emp.days[dateFound];
                                const isAbsence = dayData.type === 'ABSENCE';
                                const isRest = dayData.type === 'REST' || dayData.label.includes('COMP');
                                if (isAbsence || isRest) {
                                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDF26E' } };
                                    cell.font = { bold: true };
                                }
                            }
                        }
                    }
                });
            });

            if (employees.length > 1) {
                sheet.mergeCells(`A${startRow}:A${startRow + employees.length - 1}`);
            }
            startRow += employees.length;
        });

        sheet.getColumn(1).width = 15;
        sheet.getColumn(2).width = 25;
        for (let i = 3; i <= headerRow.length; i++) {
            sheet.getColumn(i).width = 12;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Borrador_Turnos.xlsx');
    };

    return (
        <Box sx={{ mt: 3, fontFamily: 'Arial, sans-serif' }}>
            {/* Header Title */}
            <Box sx={{ display: 'flex', alignItems: 'stretch', mb: 0.5 }}>
                <Box sx={{ 
                    backgroundColor: '#0f3c7a', 
                    color: 'white', 
                    p: 1.5, 
                    flexGrow: 1,
                    textAlign: 'center', 
                    borderRadius: '8px 0 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ width: 150 }} /> {/* Spacer */}
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 1, flex: 1 }}>
                        HORARIO SEMANAL – 44 HORAS EFECTIVAS
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="success" 
                        startIcon={<DownloadIcon />} 
                        onClick={exportToExcel}
                        sx={{ fontWeight: 'bold', width: 180, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
                    >
                        Exportar Excel
                    </Button>
                </Box>
                <Box sx={{ 
                    backgroundColor: '#e8f0fe', 
                    color: '#0f3c7a',
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0 8px 0 0'
                }}>
                    <AccessTimeIcon sx={{ fontSize: 40 }} />
                </Box>
            </Box>

            {/* Subheader */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: '#333', fontWeight: 600 }}>
                    Jornada incluye <span style={{ color: '#e76f51', fontWeight: 700 }}>30 minutos de refrigerio</span> no remunerado por día.
                </Typography>
            </Box>
            
            <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', mb: 3 }}>
                <Table size="small" sx={{ minWidth: 1000, '& .MuiTableCell-root': { border: '1px solid #dcdcdc' } }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#0f3c7a' }}>
                            <TableCell align="center" sx={{ color: 'white', fontWeight: 800, width: '8%' }}>ÁREA</TableCell>
                            <TableCell align="center" sx={{ color: 'white', fontWeight: 800, width: '12%' }}>NOMBRE</TableCell>
                            {weeks.map(([wk, wkDates], wkIdx) => (
                                <React.Fragment key={wk}>
                                    {wkDates.map((date) => (
                                        <TableCell key={date} align="center" sx={{ color: 'white', fontWeight: 800, width: '9%' }}>
                                            {getDayName(date)}<br/>
                                            <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>{dayjs(date).format('DD/MM')}</span>
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ color: '#fff', fontWeight: 900, width: '8%', lineHeight: 1.1, backgroundColor: '#e65100', borderLeft: '2px solid #bf360c !important', borderRight: '2px solid #bf360c !important' }}>
                                        TOTAL<br/>S{wkIdx + 1}
                                    </TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(groupedByArea).map(([area, employees], areaIndex) => {
                            const areaColors = ['#f4a261', '#85c179', '#5ea8cd', '#e76f51', '#e9c46a'];
                            const areaBg = areaColors[areaIndex % areaColors.length];
                            
                            return (
                                <React.Fragment key={area}>
                                    {employees.map((emp, empIndex) => (
                                        <TableRow key={emp.id} sx={{ backgroundColor: empIndex % 2 === 0 ? '#fdfdfd' : '#f5f5f5' }}>
                                            {empIndex === 0 && (
                                                <TableCell 
                                                    rowSpan={employees.length} 
                                                    sx={{ 
                                                        backgroundColor: areaBg, 
                                                        color: 'white', 
                                                        fontWeight: 800, 
                                                        textAlign: 'center',
                                                        fontSize: '1rem'
                                                    }}
                                                >
                                                    {area.toUpperCase()}
                                                </TableCell>
                                            )}
                                            <TableCell align="center" sx={{ fontWeight: 800, color: '#333', px: 1 }}>
                                                {emp.name.toUpperCase()}
                                            </TableCell>
                                            
                                            {weeks.map(([wk, wkDates], wkIdx) => {
                                                const weekTotal = wkDates.reduce((sum, d) => sum + (emp.days[d]?.hours || 0), 0);
                                                const isExceededWeek = weekTotal > emp.limit_hours;
                                                
                                                return (
                                                    <React.Fragment key={wk}>
                                                        {wkDates.map((date) => {
                                                            const dayData = emp.days[date];
                                                            const isAbsence = dayData.type === 'ABSENCE';
                                                            const isRest = dayData.type === 'REST' || dayData.label.includes('COMP');
                                                            const bg = (isAbsence || isRest) ? '#fdf26e' : 'transparent';
                                                            
                                                            return (
                                                                <TableCell key={date} align="center" sx={{ backgroundColor: bg, p: 0.5 }}>
                                                                    <Box sx={{ minHeight: 60, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: (isAbsence || isRest) ? 800 : 500, whiteSpace: 'pre-line', lineHeight: 1.2, color: '#333' }}>
                                                                            {dayData.label}
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: '#0f3c7a', fontWeight: 700, mt: 0.5 }}>
                                                                            {dayData.hours > 0 ? `${dayData.hours} h` : '0 h'}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                            )
                                                        })}
                                                        <TableCell align="center" sx={{ 
                                                            fontWeight: 900, 
                                                            fontSize: '1.2rem',
                                                            color: isExceededWeek ? '#c62828' : '#e65100',
                                                            backgroundColor: isExceededWeek ? '#ffebee' : '#fff3e0',
                                                            borderLeft: '2px solid #f57c00 !important',
                                                            borderRight: '2px solid #f57c00 !important'
                                                        }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 900, color: isExceededWeek ? '#c62828' : '#e65100', fontSize: '1.1rem' }}>
                                                                {weekTotal}h {isExceededWeek ? '*' : ''}
                                                            </Typography>
                                                        </TableCell>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6, lg: 4}}>
                    <Card variant="outlined" sx={{ height: 260, display: 'flex', flexDirection: 'column', borderColor: '#dcdcdc', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f3c7a', mb: 1, textAlign: 'center' }}>
                                ¿CÓMO LEER ESTE HORARIO?
                            </Typography>
                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <CalendarMonthIcon sx={{ color: '#0f3c7a' }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        Cada turno incluye <b>30 minutos de refrigerio</b> no remunerado.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <AccessTimeIcon sx={{ color: '#0f3c7a' }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        Las horas mostradas son las <span style={{ color: '#0f3c7a', fontWeight: 'bold' }}>horas efectivas de trabajo</span> por día (descontando el refrigerio).
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <PersonIcon sx={{ color: '#0f3c7a' }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        La jornada efectiva semanal es de <b>44 horas</b> como máximo en este período.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <BalanceIcon sx={{ color: '#0f3c7a' }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        Cualquier hora que exceda 44 horas se considera <span style={{ color: '#c62828', fontWeight: 'bold' }}>hora extra</span>.
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 12, md: 6, lg: 4}}>
                    <Card variant="outlined" sx={{ height: 260, display: 'flex', flexDirection: 'column', borderColor: '#dcdcdc', borderRadius: 2 }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f3c7a', mb: 1, textAlign: 'center' }}>
                                LEYENDA
                            </Typography>
                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ width: 40, height: 24, border: '1px solid #ccc', borderRadius: 1 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Día laborado</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ width: 40, height: 24, backgroundColor: '#fdf26e', border: '1px solid #e0d04c', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>COMP.D5</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Compensatorio otorgado por la empresa</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ width: 40, height: 24, backgroundColor: '#fdf26e', border: '1px solid #e0d04c', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.6rem' }}>DESC.</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Día de descanso</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ width: 40, height: 24, backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                        <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>-</Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#0f3c7a', lineHeight: 1 }}>0 h</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>No aplica / No trabaja ese día</Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{xs: 12, md: 12, lg: 4}}>
                    <Card variant="outlined" sx={{ height: 260, display: 'flex', flexDirection: 'column', borderColor: '#0f3c7a', borderRadius: 2 }}>
                        <Box sx={{ backgroundColor: '#0f3c7a', p: 1, textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'white' }}>
                                RESUMEN SEMANAL DE HORAS EFECTIVAS
                            </Typography>
                        </Box>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, overflowY: 'auto', flexGrow: 1 }}>
                            <Stack spacing={1}>
                                {scheduleData.map(emp => (
                                    <Box key={emp.id} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', pb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isExceeded(emp) ? (
                                                <WarningIcon sx={{ color: '#c62828', fontSize: 18 }} />
                                            ) : (
                                                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                                            )}
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {emp.name.toUpperCase()}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            {getEmpWeeklyTotals(emp).map((t, idx) => (
                                                <Typography key={idx} variant="body2" sx={{ fontWeight: 800, color: t > emp.limit_hours ? '#c62828' : '#2e7d32' }}>
                                                    Sem. {idx + 1}: {t} h {t > emp.limit_hours ? '*' : ''}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {scheduleData.some(isExceeded) && (
                <Box sx={{ mt: 2, backgroundColor: '#ffebee', p: 1.5, borderRadius: 2, border: '1px solid #ffcdd2' }}>
                    <Typography variant="body2" sx={{ color: '#c62828', fontSize: '0.8rem' }}>
                        * Hay empleados que exceden la jornada máxima de horas efectivas semanales. Se recomienda ajustar uno de sus turnos.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
